// packages/apps/workoutsmart/src/components/events/SessionCompletionMonitor.jsx

import React, { useEffect, useRef, useMemo } from 'react';
import { useResources } from '@platform/core/src/context/ResourceContext';
import { useSession } from '@platform/core/src/context/SessionContext';
import WorkoutSessionReportMessage from './WorkoutSessionReportMessage';

const SessionCompletionMonitor = () => {
  const { resources } = useResources();
  const { sessionId, addTimelineItem } = useSession();
  const prevStatusRef = useRef(null);
  const prevIdRef = useRef(null); // 1. Track the ID

  // 2. Select the most relevant session deterministically
  const activeSession = useMemo(() => {
    const sessions = resources.filter(
      r => (r.type === 'workout_session' || r.resource_type === 'workout_session') &&
           r.chat_session_id === sessionId
    );
    
    // Sort by created_at descending to get the newest one, or prioritize IN_PROGRESS
    return sessions.sort((a, b) => {
        // Prioritize active sessions
        if (a.status === 'IN_PROGRESS' && b.status !== 'IN_PROGRESS') return -1;
        if (b.status === 'IN_PROGRESS' && a.status !== 'IN_PROGRESS') return 1;
        // Otherwise newness
        return new Date(b.created_at) - new Date(a.created_at);
    })[0];
  }, [resources, sessionId]);

  useEffect(() => {
    if (!activeSession) return;

    const currentStatus = activeSession.status;
    const currentId = activeSession.id;
    
    // 3. Reset logic if the session ID changes (e.g. switching between sessions)
    if (prevIdRef.current !== currentId) {
        prevStatusRef.current = currentStatus;
        prevIdRef.current = currentId;
        return;
    }

    const prevStatus = prevStatusRef.current;

    if (prevStatus && prevStatus !== 'COMPLETED' && currentStatus === 'COMPLETED') {
      addTimelineItem({
        source: 'APP_EVENT',
        type: 'message',
        content: <WorkoutSessionReportMessage sessionId={activeSession.id} />,
        timestamp: new Date().toISOString(),
      });
    }

    prevStatusRef.current = currentStatus;
  }, [activeSession?.status, activeSession?.id, sessionId, addTimelineItem]);

  // Initial setup
  useEffect(() => {
    if (activeSession && prevStatusRef.current === null) {
      prevStatusRef.current = activeSession.status;
      prevIdRef.current = activeSession.id;
    }
  }, [activeSession]);

  return null;
};

export default SessionCompletionMonitor;